import { getAccessForProject, isAccessDeniedError } from '@infosaas/shared'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useParams } from 'react-router-dom'
import { FullPageLoader, FullPageMessage } from '../components/FullPageState'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

/**
 * Valida o acesso do cliente atual ao `projectId` da rota. Roda depois do
 * <RequireAuth />.
 *
 * Mudança importante: antes qualquer erro (rede, 500) virava "AccessDenied".
 * Agora diferencia 403/RLS (sem acesso) de erro de conexão (com retry).
 */
export function RequireProjectAccess({ children }) {
  const { profile } = useAuth()
  const { id } = useParams()
  const projectId = Number(id)
  const enabled = !!profile && Number.isFinite(projectId)

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['project-access', profile?.id, projectId],
    queryFn: () => getAccessForProject(supabase, profile.id, projectId),
    enabled,
    staleTime: 5 * 60_000,
  })

  if (!Number.isFinite(projectId)) return <Navigate to="/" replace />
  if (isLoading || !enabled) return <FullPageLoader label="Verificando acesso..." />

  if (error && !isAccessDeniedError(error)) {
    return (
      <FullPageMessage
        title="Falha de conexão"
        message={error.message ?? 'Não foi possível verificar seu acesso. Verifique sua internet.'}
        action={
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-6 rounded-2xl bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {isFetching ? 'Tentando novamente...' : 'Tentar novamente'}
          </button>
        }
      />
    )
  }

  if (error || !data) {
    return (
      <FullPageMessage
        title="Sem acesso a este projeto"
        message={
          'Você não tem permissão para visualizar este projeto.\n' +
          'Se acredita ser um erro, entre em contato com seu gerente na Infosaas.'
        }
      />
    )
  }

  return children
}
